import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { api, fetchBlobUrl } from "../api/client";
import type { Project } from "../api/types";
import { Icon } from "../components/icons";
import { ProjectHeader } from "../components/ProjectHeader";
import { RunPanel } from "../components/RunPanel";
import { toast } from "../components/toast";
import {
  CELL_COLUMNS,
  COLUMN_LABELS,
  assumedCorrect,
  cellAddress,
  cellNote,
  endSentence,
  flagged,
  formatCells,
  manuscriptPageUrl,
  needsReview,
  openFlags,
  reviewStats,
  settled,
  ticked,
  toolFlag,
  toolReason,
  useToc,
} from "../lib/toc";
import type { CellColumn, TocPayload, TocRow } from "../lib/toc";
import { backdropClose } from "../lib/backdrop";

// Table of Contents review (toc module). The tool's verdict applies by
// default and the reviewer only clicks to disagree — so this reads as marking
// someone's work, not filling in a form. Every tested cell carries a flag and
// a review tick; cells the tool can't settle are shaded to say "you need to
// look at this".

const REPORT_ORDER = [
  "format",
  "document_name",
  "product_id",
  "chapter_name",
  "hierarchy",
  "audio_track",
  "timestamp",
];

function seconds(timestamp: string): number | null {
  const m = /^(\d{2}):(\d{2}):(\d{2})[.:](\d{1,3})$/.exec(timestamp.trim());
  if (!m) return null;
  return Number(m[1]) * 3600 + Number(m[2]) * 60 + Number(m[3]) + Number(m[4].padEnd(3, "0")) / 1000;
}

/** Seconds back to the ToC's own HH:MM:SS.mmm, so a nudged playhead can be
    saved as the corrected value without retyping it. */
function fmtTimestamp(total: number): string {
  const t = Math.max(0, total);
  const pad = (n: number, w = 2) => String(Math.floor(n)).padStart(w, "0");
  return `${pad(t / 3600)}:${pad((t / 60) % 60)}:${pad(t % 60)}.${pad(
    Math.round((t % 1) * 1000), 3
  )}`;
}

export default function TableOfContents() {
  const { projectId = "" } = useParams();
  const { toc, isLoading, edit, uploadToc, remove, refresh } = useToc(projectId);
  const [note, setNote] = useState<{ col: CellColumn; row: TocRow } | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  // The manuscript page the reviewer is glance-checking, and the chapter it was
  // opened for (shown in the modal caption). null == closed.
  const [pageView, setPageView] = useState<{ page: number; label: string } | null>(null);

  // Upload, and if the server auto-started the manuscript read, follow that
  // job so its comparison lands here without a reload.
  function doUpload(file: File) {
    uploadToc.mutate(file, {
      onSuccess: (fresh) => {
        if (fresh.extraction_job_id) setJobId(fresh.extraction_job_id);
      },
    });
  }

  const { data: project } = useQuery({
    queryKey: ["project", projectId],
    queryFn: () => api<Project>(`/api/projects/${projectId}`),
  });

  const extract = useMutation({
    // Jobs are created under the project, and the response carries `job_id` —
    // same shape the Spec-check page uses. (An earlier POST to /api/jobs with a
    // project_id in the body 405'd — that route doesn't exist.)
    mutationFn: () =>
      api<{ job_id: string }>(`/api/projects/${projectId}/jobs`, {
        method: "POST",
        body: JSON.stringify({
          module_id: "toc",
          job_type: "toc_extract",
          params: {},
        }),
      }),
    onSuccess: (r) => setJobId(r.job_id),
    onError: (e: Error) => toast(e.message, "error"),
  });

  return (
    <>
      <ProjectHeader
        projectId={projectId}
        name={project?.name ?? ""}
        active="toc"
        right={
          toc?.filename ? (
            <>
              <label className="btn ghost sm toc-replace">
                <Icon name="upload" size={14} />
                Replace
                <input
                  type="file"
                  accept=".xlsx"
                  hidden
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) doUpload(file);
                    e.target.value = "";
                  }}
                />
              </label>
              <button
                className="btn ghost sm"
                onClick={() => {
                  if (confirm("Remove this Table of Contents and its review?")) remove.mutate();
                }}
              >
                <Icon name="trash" size={14} />
                Remove
              </button>
            </>
          ) : undefined
        }
      />

      {/* With the manuscript open the page becomes a split screen: the script
          sits in a sticky, independently-scrolling left column and the whole ToC
          review stays live on the right — so you can read on while working the
          table, instead of a modal that blocks it. */}
      <div className={`page toc-page${pageView ? " toc-split" : ""}`}>
        {pageView && (
          <ManuscriptDrawer
            projectId={projectId}
            page={pageView.page}
            label={pageView.label}
            nPages={toc?.expected?.n_pages ?? 0}
            onClose={() => setPageView(null)}
          />
        )}
        <div className="toc-splitmain">
        {isLoading && <p className="muted">Loading…</p>}

        {!isLoading && !toc?.filename && (
          <UploadPanel
            busy={uploadToc.isPending}
            onFile={(file) => doUpload(file)}
            error={uploadToc.error?.message}
          />
        )}

        {toc?.filename && (
          <>
            <div className="toc-filebar">
              <Icon name="table" size={16} className="muted" />
              <code>{toc.filename}</code>
              {toc.uploaded_at && (
                <span className="muted">
                  uploaded {new Date(toc.uploaded_at).toLocaleDateString()}
                </span>
              )}
            </div>

            {toc.fatal ? (
              <div className="toc-fatal">
                <Icon name="alert" size={18} />
                <div>
                  <strong>This workbook could not be read.</strong>
                  <p>{toc.fatal}</p>
                </div>
              </div>
            ) : (
              <>
                <SummaryTable toc={toc} />
                <DocumentChecks toc={toc} />
                <CellsTable
                  toc={toc}
                  projectId={projectId}
                  edit={edit}
                  onNote={(col, row) => setNote({ col, row })}
                  onViewPage={(page, label) => setPageView({ page, label })}
                />
              </>
            )}
          </>
        )}

        {toc && !toc.fatal && (
          <ExpectedPanel
            toc={toc}
            running={!!jobId}
            hasManuscript={!!project?.manuscript_filename}
            onExtract={() => extract.mutate()}
            onViewPage={(page, label) => setPageView({ page, label })}
          />
        )}

        {jobId && (
          <RunPanel
            key={jobId}
            jobId={jobId}
            title="Reading the manuscript"
            onDone={() => {
              setJobId(null);
              refresh(); // pull in the expected list + comparison the read produced
            }}
          />
        )}

        {toc?.filename && !toc.fatal && <Report toc={toc} edit={edit} />}
        </div>
      </div>

      {note && toc && (
        <NoteModal
          toc={toc}
          col={note.col}
          row={note.row}
          onClose={() => setNote(null)}
          edit={edit}
        />
      )}

    </>
  );
}

// --- empty state -------------------------------------------------------------

function UploadPanel({
  onFile,
  busy,
  error,
}: {
  onFile: (f: File) => void;
  busy: boolean;
  error?: string;
}) {
  const [over, setOver] = useState(false);
  return (
    <div
      className={`toc-drop${over ? " over" : ""}`}
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        const file = e.dataTransfer.files?.[0];
        if (file) onFile(file);
      }}
    >
      <Icon name="table" size={36} className="muted" />
      <h3>Add the Table of Contents</h3>
      <p className="muted">
        Drop the producer's <code>productID_ChapterData_YYMMDD.xlsx</code> here, or
      </p>
      <label className="btn primary">
        {busy ? "Uploading…" : "Choose file"}
        <input
          type="file"
          accept=".xlsx"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) onFile(file);
            e.target.value = "";
          }}
        />
      </label>
      {error && <p className="toc-drop-err">{error}</p>}
    </div>
  );
}

// --- summary -----------------------------------------------------------------

function SummaryTable({ toc }: { toc: TocPayload }) {
  const stats = reviewStats(toc);
  const done = stats.toReview === 0;
  return (
    <table className="toc-stats">
      <tbody>
        <tr>
          <th>Needs your review</th>
          <td className={stats.toReview ? "warn" : "ok"}>
            {stats.toReview ? `${stats.toReview} cell${stats.toReview === 1 ? "" : "s"}` : "None"}
          </td>
          <th>Issues flagged</th>
          <td className={stats.flags ? "bad" : "ok"}>{stats.flags}</td>
          <th>Heard by ear</th>
          <td>
            {stats.heard} / {stats.hearable}
          </td>
          <th>Report</th>
          <td className={done ? "ok" : ""}>{done ? "Ready" : "In review"}</td>
        </tr>
      </tbody>
    </table>
  );
}

// --- document-level checks ---------------------------------------------------

function DocumentChecks({ toc }: { toc: TocPayload }) {
  const docs = toc.metrics.filter((m) => m.id === "format" || m.id === "document_name");
  return (
    <div className="toc-docchecks">
      {docs.map((m) => (
        <div key={m.id} className={`toc-doccheck ${m.verdict === false ? "bad" : m.verdict ? "ok" : ""}`}>
          <div className="toc-doccheck-head">
            <Icon name={m.verdict === false ? "alert" : "check"} size={14} />
            <strong>{m.label}</strong>
          </div>
          <p>{m.comment}</p>
        </div>
      ))}
    </div>
  );
}

// --- the grid ----------------------------------------------------------------

function CellsTable({
  toc,
  projectId,
  edit,
  onNote,
  onViewPage,
}: {
  toc: TocPayload;
  projectId: string;
  edit: (m: (r: import("../lib/toc").TocReviewState) => import("../lib/toc").TocReviewState) => void;
  onNote: (col: CellColumn, row: TocRow) => void;
  onViewPage: (page: number, label: string) => void;
}) {
  function toggleFlag(col: CellColumn, row: TocRow) {
    const k = String(row.index);
    const next = !flagged(toc, col, row);
    edit((r) => {
      const marks = { ...(r.marks ?? {}) };
      const column = { ...(marks[col] ?? {}) };
      // Matching the tool again means "no override", so drop the entry rather
      // than storing agreement.
      if (next === toolFlag(toc, col, row)) delete column[k];
      else column[k] = next;
      marks[col] = column;
      // Touching the flag IS reviewing the cell — saves a second click.
      const ticks = { ...(r.ticks ?? {}) };
      ticks[col] = { ...(ticks[col] ?? {}), [k]: true };
      return { ...r, marks, ticks };
    });
  }

  function toggleTick(col: CellColumn, row: TocRow) {
    const k = String(row.index);
    edit((r) => {
      const ticks = { ...(r.ticks ?? {}) };
      const column = { ...(ticks[col] ?? {}) };
      if (column[k]) delete column[k];
      else column[k] = true;
      ticks[col] = column;
      return { ...r, ticks };
    });
  }

  /** Flag (or clear) every cell in a column at once. */
  function flagColumn(col: CellColumn, on: boolean) {
    edit((r) => {
      const marks = { ...(r.marks ?? {}) };
      const ticks = { ...(r.ticks ?? {}) };
      const mc: Record<string, boolean> = {};
      const tc = { ...(ticks[col] ?? {}) };
      for (const row of toc.rows) {
        const k = String(row.index);
        // Only store a mark where it differs from the tool's own verdict, the
        // same as a single click — so clearing a column returns those cells to
        // the tool's opinion rather than freezing today's answer.
        if (on !== toolFlag(toc, col, row)) mc[k] = on;
        tc[k] = true;
      }
      marks[col] = mc;
      ticks[col] = tc;
      return { ...r, marks, ticks };
    });
  }

  /** Mark a whole column reviewed without changing any verdict. */
  function confirmColumn(col: CellColumn, on: boolean) {
    edit((r) => {
      const ticks = { ...(r.ticks ?? {}) };
      ticks[col] = on
        ? Object.fromEntries(toc.rows.map((row) => [String(row.index), true]))
        : {};
      return { ...r, ticks };
    });
  }

  return (
    <div className="toc-tablewrap">
      <table className="toc-cells">
        <thead>
          <tr>
            <th className="toc-rownum">Row</th>
            {CELL_COLUMNS.map((c) => {
              const allFlagged =
                toc.rows.length > 0 && toc.rows.every((r) => flagged(toc, c, r));
              const allTicked =
                toc.rows.length > 0 && toc.rows.every((r) => ticked(toc, c, r));
              return (
                <th key={c} className={`toc-col-${c}`}>
                  <div className="toc-colhead">
                    <span>{COLUMN_LABELS[c]}</span>
                    <span className="toc-colactions">
                      <button
                        className={`toc-colbtn${allFlagged ? " on" : ""}`}
                        onClick={() => flagColumn(c, !allFlagged)}
                        title={allFlagged
                          ? `Clear every flag in ${COLUMN_LABELS[c]}`
                          : `Flag every cell in ${COLUMN_LABELS[c]}`}
                      >
                        <Icon name="flag" size={11} />
                        All
                      </button>
                      <button
                        className={`toc-colbtn${allTicked ? " on" : ""}`}
                        onClick={() => confirmColumn(c, !allTicked)}
                        title={allTicked
                          ? `Mark ${COLUMN_LABELS[c]} as not reviewed`
                          : `Mark every cell in ${COLUMN_LABELS[c]} reviewed`}
                      >
                        <Icon name="check" size={11} />
                        All
                      </button>
                    </span>
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {toc.rows.map((row) => (
            <tr key={row.index}>
              <td className="toc-rownum">{row.sheet_row}</td>
              {CELL_COLUMNS.map((col) => (
                <Cell
                  key={col}
                  toc={toc}
                  col={col}
                  row={row}
                  projectId={projectId}
                  onFlag={() => toggleFlag(col, row)}
                  onTick={() => toggleTick(col, row)}
                  onNote={() => onNote(col, row)}
                  onViewPage={onViewPage}
                  edit={edit}
                />
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function cellValue(col: CellColumn, row: TocRow): string {
  if (col === "accuracy") return row.timestamp;
  if (col === "hierarchy") return row.hierarchy === "" ? "—" : row.hierarchy;
  return (row as unknown as Record<string, string>)[col] ?? "";
}

function Cell({
  toc,
  col,
  row,
  projectId,
  onFlag,
  onTick,
  onNote,
  onViewPage,
  edit,
}: {
  toc: TocPayload;
  col: CellColumn;
  row: TocRow;
  projectId: string;
  onFlag: () => void;
  onTick: () => void;
  onNote: () => void;
  onViewPage: (page: number, label: string) => void;
  edit: (m: (r: import("../lib/toc").TocReviewState) => import("../lib/toc").TocReviewState) => void;
}) {
  const isFlagged = flagged(toc, col, row);
  const review = needsReview(toc, col, row);
  const isTicked = ticked(toc, col, row) || settled(toc, col, row);
  const note = cellNote(toc, col, row);
  const reason = toolReason(toc, col, row);

  const classes = [
    "toc-cell",
    `toc-col-${col}`,
    isFlagged ? "flag" : "",
    review ? "review" : "",
    assumedCorrect(toc, col, row) ? "assumed" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <td className={classes}>
      <div className="toc-cell-val">{cellValue(col, row)}</div>

      {col === "accuracy" && (
        row.audio_file ? (
          <>
            <TimestampPlayer toc={toc} row={row} projectId={projectId} edit={edit} />
            {row.audio_match === "assumed" && (
              <div
                className="toc-assumed"
                title={`The ToC's audio names don't match the delivered files, so this row was lined up by order to "${row.audio_name || row.audio_file}". Check it's the right file.`}
              >
                assumed: {row.audio_name || row.audio_file}
              </div>
            )}
          </>
        ) : (
          <div className="toc-noaudio">
            {!toc.audio_available
              ? "no audio uploaded yet"
              : !row.audio_track
              ? "no audio file named"
              : "no matching audio file"}
          </div>
        )
      )}

      <div className="toc-cell-controls">
        {col === "chapter_name" && row.manuscript_page && (
          <button
            className="toc-pagebtn"
            onClick={() => onViewPage(row.manuscript_page as number, row.chapter_name)}
            title={`Show manuscript p.${row.manuscript_page}, where the tool matched this chapter — check the title against the page`}
          >
            <Icon name="scan-text" size={12} />
          </button>
        )}
        <button
          className={`toc-flag${isFlagged ? " on" : ""}`}
          onClick={onFlag}
          title={isFlagged ? "Clear this flag" : "Flag an issue here"}
          aria-pressed={isFlagged}
        >
          <Icon name="flag" size={12} />
        </button>
        <button
          className={`toc-tick${isTicked ? " on" : ""}`}
          onClick={onTick}
          title={isTicked ? "Mark as not reviewed" : "Mark as reviewed"}
          aria-pressed={isTicked}
        >
          <Icon name="check" size={12} />
        </button>
        {isFlagged && (
          <button
            className={`toc-note${note ? " has" : ""}`}
            onClick={onNote}
            title={note ? "Edit your note" : "Add a note for the report"}
          >
            <Icon name="notebook" size={12} />
          </button>
        )}
      </div>

      {isFlagged && (reason || note) && (
        <div className="toc-cell-why">{note || reason}</div>
      )}
    </td>
  );
}

// --- the ear test ------------------------------------------------------------

function TimestampPlayer({
  toc,
  row,
  projectId,
  edit,
}: {
  toc: TocPayload;
  row: TocRow;
  projectId: string;
  edit: (m: (r: import("../lib/toc").TocReviewState) => import("../lib/toc").TocReviewState) => void;
}) {
  const ref = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const k = String(row.index);
  const heard = !!toc.review.heard?.[k];
  const corrected = toc.review.timestamps?.[k];
  const value = corrected ?? row.timestamp;
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  // Where the audio actually is. Runs while playing and STAYS put on pause, so
  // you can roll back past a suspect chapter start, stop, and read the true
  // timecode off — which is the number you'd give the producer.
  const [now, setNow] = useState<number | null>(null);

  const src = `/api/projects/${projectId}/audio/${encodeURIComponent(row.audio_file)}/stream`;

  /** Start fetching the file's header before it's needed. On production the
      first play pays a redirect to a presigned URL plus a range request just
      to read the header; doing that on hover means the click only has to
      buffer at the seek point, which is most of the felt latency gone. */
  function warm() {
    const a = ref.current;
    if (a && a.preload !== "metadata" && a.readyState === 0) {
      a.preload = "metadata";
      a.load();
    }
  }

  function markHeard() {
    if (heard) return;
    edit((r) => ({
      ...r,
      heard: { ...(r.heard ?? {}), [k]: true },
      // Listening IS reviewing — tick the accuracy cell off at the same time.
      ticks: { ...(r.ticks ?? {}), accuracy: { ...(r.ticks?.accuracy ?? {}), [k]: true } },
    }));
  }

  /** Move the playhead AND the field with it — the field shows where you are,
      so nudging ±10s to find the true start leaves the corrected value ready
      to save. */
  function moveTo(at: number, play: boolean) {
    const a = ref.current;
    if (!a) return;
    const target = Math.max(0, at);
    const applySeek = () => {
      a.currentTime = target;
      setNow(target);
    };
    if (a.readyState >= 1) {
      applySeek();
    } else {
      // No metadata yet, so a currentTime set now is discarded — seek once the
      // header arrives. Under preload="none", play() is what actually forces
      // the fetch; a bare load() may never fire loadedmetadata, so DON'T wait
      // for metadata before starting playback.
      a.addEventListener("loadedmetadata", applySeek, { once: true });
      if (!play) warm(); // a ±10s nudge before any play still needs the header
    }
    if (play) {
      a.play().then(markHeard).catch((e) => {
        setLoading(false);
        setError(e?.message || "Could not play this file");
      });
    }
  }

  function seek(delta: number) {
    const a = ref.current;
    if (!a) return;
    moveTo((a.readyState >= 1 ? a.currentTime : seconds(draft) ?? 0) + delta, false);
  }

  function play() {
    const a = ref.current;
    if (!a) return;
    if (!a.paused) {
      a.pause();
      return;
    }
    setError(null);
    if (a.readyState < 3) setLoading(true); // not enough buffered to play yet
    // Resume where the playhead already is (after a ±10s nudge); otherwise
    // start at the timestamp under review.
    moveTo(a.readyState >= 1 && a.currentTime > 0 ? a.currentTime : seconds(draft) ?? 0, true);
  }

  /** Take the paused playhead as the corrected timestamp — the deliberate way to
      fix a producer's value, now that nudging leaves their number alone. */
  function usePlayhead() {
    if (now === null) return;
    const ts = fmtTimestamp(now);
    setDraft(ts);
    edit((r) => ({ ...r, timestamps: { ...(r.timestamps ?? {}), [k]: ts } }));
  }

  return (
    <div className="toc-player" onMouseEnter={warm}>
      <audio
        ref={ref}
        src={src}
        preload="none"
        onPlay={(e) => {
          setPlaying(true);
          // Only ONE mini-player runs at a time: starting this row stops whichever
          // was playing. Their own `onPause` resets their button, so the UI follows.
          const me = e.currentTarget;
          document.querySelectorAll<HTMLAudioElement>(".toc-player audio").forEach((other) => {
            if (other !== me && !other.paused) other.pause();
          });
        }}
        onPlaying={() => setLoading(false)}
        onTimeUpdate={(e) => setNow(e.currentTarget.currentTime)}
        onSeeked={(e) => setNow(e.currentTarget.currentTime)}
        onPause={() => setPlaying(false)}
        onError={() => {
          setLoading(false);
          setError(
            `"${row.audio_track}" could not be loaded — check it matches a delivered audio file.`
          );
        }}
      />
      <button className="toc-tp" onClick={() => seek(-10)} title="Back 10 seconds">
        −10s
      </button>
      <button
        className={`toc-tp play${playing ? " on" : ""}${loading ? " loading" : ""}`}
        onClick={play}
        title={loading ? "Loading…" : playing ? "Pause" : "Play from this timestamp"}
      >
        <Icon name={playing ? "pause" : "play"} size={12} />
      </button>
      <button className="toc-tp" onClick={() => seek(10)} title="Forward 10 seconds">
        +10s
      </button>
      <input
        className={`toc-tsfield${corrected ? " edited" : ""}`}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          if (draft === row.timestamp) {
            if (corrected === undefined) return;
            edit((r) => {
              const next = { ...(r.timestamps ?? {}) };
              delete next[k];
              return { ...r, timestamps: next };
            });
            return;
          }
          if (draft !== corrected) {
            edit((r) => ({ ...r, timestamps: { ...(r.timestamps ?? {}), [k]: draft } }));
          }
        }}
        title="The producer's timestamp. Edit it directly, or play and Use the live timecode."
      />
      {now !== null && (
        <span className={`toc-now${playing ? " live" : ""}`}
          title="Where the audio is right now. Pause where the chapter really starts, then Use it.">
          {fmtTimestamp(now)}
          {fmtTimestamp(now) !== draft && (
            <button className="toc-nowuse" onClick={usePlayhead}
              title="Record this as the corrected timestamp">use</button>
          )}
        </span>
      )}
      {heard && !error && <span className="toc-heard">✓ heard</span>}
      {/* A silent player with no explanation is worse than an error. */}
      {error && <span className="toc-playerr" title={error}>can't play</span>}
    </div>
  );
}

// --- note modal --------------------------------------------------------------

function NoteModal({
  toc,
  col,
  row,
  onClose,
  edit,
}: {
  toc: TocPayload;
  col: CellColumn;
  row: TocRow;
  onClose: () => void;
  edit: (m: (r: import("../lib/toc").TocReviewState) => import("../lib/toc").TocReviewState) => void;
}) {
  const k = String(row.index);
  const [text, setText] = useState(cellNote(toc, col, row));
  const area = useRef<HTMLTextAreaElement | null>(null);
  useEffect(() => area.current?.focus(), []);

  function save() {
    edit((r) => {
      const comments = { ...(r.comments ?? {}) };
      const column = { ...(comments[col] ?? {}) };
      if (text.trim()) column[k] = text.trim();
      else delete column[k];
      comments[col] = column;
      return { ...r, comments };
    });
    onClose();
  }

  return (
    <div className="modal-overlay" {...backdropClose(onClose)}>
      <div className="modal-card toc-notemodal" onClick={(e) => e.stopPropagation()}>
        <h3>
          Note on {COLUMN_LABELS[col]} — row {row.sheet_row}
        </h3>
        <p className="muted">This is included in the report, after the tool's own comment.</p>
        <textarea
          ref={area}
          rows={4}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              onClose();
              return;
            }
            if (e.key !== "Enter") return;
            if (e.metaKey || e.ctrlKey) {
              // ⌘/Ctrl+Enter inserts a newline; plain Enter saves and closes.
              const el = e.currentTarget;
              const at = el.selectionStart;
              setText(`${text.slice(0, at)}\n${text.slice(el.selectionEnd)}`);
              requestAnimationFrame(() => el.setSelectionRange(at + 1, at + 1));
              e.preventDefault();
              return;
            }
            e.preventDefault();
            save();
          }}
        />
        <div className="modal-actions">
          <button className="btn ghost" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" onClick={save}>
            Save note
          </button>
        </div>
      </div>
    </div>
  );
}

// --- manuscript drawer (split screen) ----------------------------------------

/** One rendered manuscript page, fetched as an authed blob (a bare <img src> to
    the endpoint would 401 under Clerk) and revoked on unmount. */
function ManuscriptPage({ projectId, page }: { projectId: string; page: number }) {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let objUrl: string | null = null;
    setUrl(null);
    setError(null);
    fetchBlobUrl(manuscriptPageUrl(projectId, page))
      .then((u) => {
        if (cancelled) { URL.revokeObjectURL(u); return; }
        objUrl = u;
        setUrl(u);
      })
      .catch((e: Error) => { if (!cancelled) setError(e.message); });
    return () => {
      cancelled = true;
      if (objUrl) URL.revokeObjectURL(objUrl);
    };
  }, [projectId, page]);

  return (
    <figure className="toc-mpage">
      {error ? (
        <p className="toc-playerr">{error}</p>
      ) : url ? (
        <img src={url} alt={`Manuscript page ${page}`} />
      ) : (
        <div className="toc-mpage-ph">Loading p.{page}…</div>
      )}
      <figcaption>p.{page}</figcaption>
    </figure>
  );
}

/**
 * The manuscript as a SPLIT-SCREEN left column rather than a modal: it stays open
 * while you work the ToC review on the right, and scrolls continuously — pages
 * load as you reach them, so you can read past the matched heading instead of
 * flipping one page at a time. Opens anchored on the page the tool matched, and
 * re-anchors when you open it from another chapter.
 */
function ManuscriptDrawer({
  projectId, page, label, nPages, onClose,
}: {
  projectId: string; page: number; label: string; nPages: number; onClose: () => void;
}) {
  const [from, setFrom] = useState(page);
  const [count, setCount] = useState(3);
  const bodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setFrom(page);
    setCount(3);
    bodyRef.current?.scrollTo({ top: 0 });
  }, [page]);

  const last = nPages > 0 ? Math.min(from + count - 1, nPages) : from + count - 1;
  const pages: number[] = [];
  for (let p = from; p <= last; p++) pages.push(p);
  const more = nPages === 0 || last < nPages;

  /** Reaching the bottom pulls in the next few pages — continuous reading. */
  function onScroll() {
    const el = bodyRef.current;
    if (!el || !more) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 240) setCount((c) => c + 3);
  }

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === "Escape") onClose(); }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <aside className="toc-mdrawer" aria-label="Manuscript">
      <div className="toc-mdrawer-head">
        <div className="toc-mdrawer-title">
          <strong>{label}</strong>
          <span className="muted">
            matched at p.{page}{nPages > 0 ? ` · ${nPages} pages` : ""}
          </span>
        </div>
        <button className="btn ghost sm" onClick={onClose} title="Close the manuscript">
          <Icon name="x" size={16} />
        </button>
      </div>

      <div className="toc-mdrawer-body" ref={bodyRef} onScroll={onScroll}>
        {from > 1 && (
          <button className="toc-mdrawer-more" onClick={() => setFrom((f) => Math.max(1, f - 2))}>
            ▲ Earlier pages
          </button>
        )}
        {pages.map((p) => <ManuscriptPage key={p} projectId={projectId} page={p} />)}
        {more && <div className="toc-mdrawer-more muted">Scroll for more…</div>}
      </div>
    </aside>
  );
}


// --- expected list from the manuscript ---------------------------------------

function ExpectedPanel({
  toc,
  running,
  hasManuscript,
  onExtract,
  onViewPage,
}: {
  toc: TocPayload;
  running: boolean;
  hasManuscript: boolean;
  onExtract: () => void;
  onViewPage: (page: number, label: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const expected = toc.expected;
  const comparison = toc.comparison;

  return (
    <div className="toc-expected">
      <div className="toc-expected-head">
        {/* Only a toggle once there is something to expand — otherwise it reads
            as a control that does nothing when clicked. */}
        {expected ? (
          <button className="toc-expected-toggle" onClick={() => setOpen(!open)}>
            <Icon name={open ? "chevron-down" : "chevron-right"} size={14} />
            Manuscript vs the ToC
            <span className="toc-count">{expected.items.length}</span>
          </button>
        ) : (
          <span className="toc-expected-toggle as-label">Manuscript vs the ToC</span>
        )}
        <button className="btn ghost sm" onClick={onExtract} disabled={running || !hasManuscript}>
          {running ? "Reading…" : expected ? "Re-read manuscript" : "Read from manuscript"}
        </button>
      </div>

      {!expected && (
        <p className="muted toc-expected-empty">
          {hasManuscript
            ? "Read the manuscript to get the chapter list and hierarchy the ToC should " +
              "contain, then compare it against the Chapter Name and Hierarchy columns " +
              "above — mismatches are flagged there for you to confirm."
            : "This project has no manuscript. Upload the PDF to compare the ToC's " +
              "chapter names and hierarchy against what the book actually contains."}
        </p>
      )}

      {/* The load-bearing finding — a chapter the manuscript has that the ToC
          dropped — stays visible even when the list is collapsed. */}
      {comparison && comparison.missing.length > 0 && (
        <div className="toc-compare-missing">
          <Icon name="alert" size={13} />
          <span>
            {comparison.missing.length === 1
              ? "1 section is in the manuscript but not the ToC:"
              : `${comparison.missing.length} sections are in the manuscript but not the ToC:`}{" "}
            {comparison.missing.map((m, i) => (
              <span key={i}>
                {i > 0 && ", "}
                {m.page ? (
                  <button
                    className="toc-page-link"
                    onClick={() => onViewPage(m.page as number, m.name)}
                    title={`Show manuscript p.${m.page} — confirm the ToC really dropped this`}
                  >
                    {m.name}
                  </button>
                ) : (
                  m.name
                )}
              </span>
            ))}
          </span>
        </div>
      )}

      {open && expected && (
        <>
          {comparison && (
            <p className="toc-compare-summary muted">
              {comparison.matched} of {expected.items.length} manuscript sections matched a ToC
              row. Near-miss names and hierarchy differences are flagged in the columns above.
              {comparison.extra.length > 0 &&
                ` ${comparison.extra.length} ToC row${comparison.extra.length === 1 ? "" : "s"} ` +
                  "had no manuscript counterpart (usually production sections like " +
                  "Opening/End Credits): " +
                  comparison.extra.map((e) => e.name).join(", ") + "."}
            </p>
          )}
          {expected.concerns.length > 0 && (
            <ul className="toc-concerns">
              {expected.concerns.map((c, i) => (
                <li key={i}>
                  <Icon name="alert" size={12} /> {c}
                </li>
              ))}
            </ul>
          )}
          <ol className="toc-expected-list">
            {expected.items.map((item, i) => {
              const missing = comparison?.missing.some((m) => m.name === item.name);
              return (
                <li
                  key={i}
                  className={`${item.hierarchy === "" ? "lvl-part" : ""}${missing ? " missing" : ""}`}
                >
                  <span className="toc-exp-name">
                    {item.name}
                    {item.page ? (
                      <button
                        className="toc-page-inline"
                        onClick={() => onViewPage(item.page as number, item.name)}
                        title={`Show manuscript p.${item.page}`}
                      >
                        <Icon name="scan-text" size={11} /> p.{item.page}
                      </button>
                    ) : null}
                  </span>
                  <span className="toc-exp-meta">
                    {item.hierarchy === "" ? "—" : item.hierarchy} · {item.evidence}
                    {missing && <span className="toc-exp-missing"> · not in the ToC</span>}
                  </span>
                  {item.note && <span className="toc-exp-note">{item.note}</span>}
                </li>
              );
            })}
          </ol>
        </>
      )}
    </div>
  );
}

// --- report ------------------------------------------------------------------

function Report({
  toc,
  edit,
}: {
  toc: TocPayload;
  edit: (m: (r: import("../lib/toc").TocReviewState) => import("../lib/toc").TocReviewState) => void;
}) {
  const flags = useMemo(() => openFlags(toc), [toc]);
  const byColumn = useMemo(() => {
    const map: Partial<Record<CellColumn, { row: TocRow; note: string; reason: string }[]>> = {};
    for (const { col, row } of flags) {
      (map[col] ??= []).push({
        row,
        note: cellNote(toc, col, row),
        reason: toolReason(toc, col, row),
      });
    }
    return map;
  }, [flags, toc]);

  const metrics = REPORT_ORDER.map((id) => toc.metrics.find((m) => m.id === id)).filter(
    Boolean
  ) as TocPayload["metrics"];

  // Metric 7 covers the timestamp's format AND whether it lands where it should,
  // so the by-ear accuracy flags belong on the Timestamp row — they have no
  // metric of their own and would otherwise never reach the report.
  function cellsFor(metricId: string, col: CellColumn) {
    const own = byColumn[col] ?? [];
    if (metricId !== "timestamp") return own;
    // Merged from two columns, so re-sort — the reader expects sheet order.
    return [...own, ...(byColumn.accuracy ?? [])].sort((a, b) => a.row.sheet_row - b.row.sheet_row);
  }

  return (
    <div className="toc-report">
      <h2>Report</h2>
      <table className="toc-reporttable">
        <thead>
          <tr>
            <th>Metric</th>
            <th>Compliant</th>
            <th>Cells</th>
            <th>Comment</th>
          </tr>
        </thead>
        <tbody>
          {metrics.map((m) => {
            const col = m.column as CellColumn;
            const cells = cellsFor(m.id, col);
            // The reviewer's flags decide the human metrics; the tool's verdict
            // stands for the automatic ones unless it was overruled.
            const verdict = m.verdict === null ? cells.length === 0 : m.verdict && cells.length === 0;
            const written = toc.review.report_comments?.[m.id];
            const auto = [
              endSentence(m.comment),
              ...cells.map(
                (c) =>
                  endSentence(c.note || c.reason) ||
                  `Row ${c.row.sheet_row} was flagged in review.`
              ),
            ]
              .filter(Boolean)
              .join(" ");
            return (
              <tr key={m.id}>
                <td>{m.label}</td>
                <td className={verdict ? "ok" : "bad"}>{verdict ? "Yes" : "No"}</td>
                <td className="toc-report-cells">
                  {formatCells(
                    [...new Set(cells.map((c) => cellAddress(col, c.row)))],
                    toc.rows.length
                  )}
                </td>
                <td>
                  <textarea
                    className="toc-report-comment"
                    rows={2}
                    value={written ?? auto}
                    onChange={(e) => {
                      const text = e.target.value;
                      edit((r) => ({
                        ...r,
                        report_comments: { ...(r.report_comments ?? {}), [m.id]: text },
                      }));
                    }}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="muted toc-report-foot">
        Flags you clear are left out. Editing a comment replaces the generated one; clear it to go
        back to the generated text.
      </p>
    </div>
  );
}
